/**
 * Sidebar navigation for the admin dashboard.
 *
 * Navy background, dynamic agency branding, Lucide icons. The nav is grouped
 * into collapsible accordion sections (#541). On every load the nav opens
 * clean (#803): only the first group and the active route's group are
 * expanded, every other group collapsed -- no persisted "pile of open groups".
 * Collapsible on mobile (hidden by default, toggle via hamburger).
 *
 * #2579 (EPIC #2575 Tier 4) — DESKTOP ICON RAIL: shares the exact mechanism with
 * CrmSidebar via the `useSidebarCollapsed` hook + `SidebarCollapseToggle` +
 * `NavGroupSection collapsed`. The rail width applies only at `lg:`; the mobile
 * drawer is untouched.
 *
 * The nav data itself lives in `./adminNav.ts` so that the /admin/dashboard
 * quick-link grid imports the exact same labels/routes/icons/module gates --
 * single source of truth, can never drift (#872).
 */
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { ThemeToggle } from "../ThemeToggle";
import { NavGroupSection } from "./NavGroupSection";
import { SidebarCollapseToggle } from "./SidebarCollapseToggle";
import { useBranding } from "../../hooks/useBranding";
import AgencyLogo from "../AgencyLogo";
import { useNavGroups } from "../../hooks/useNavGroups";
import { useSidebarCollapsed } from "../../hooks/useSidebarCollapsed";
import { useModules } from "../../contexts/modules";
import { useAuth, useEffectiveAuth, usePermissions } from "../../contexts/auth";
import { useTour } from "../../contexts/tour";
import { rankFor } from "../../api/users";
import { adminNavGroups, type NavItem } from "./adminNav";

interface AdminSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AdminSidebar({ isOpen, onClose }: AdminSidebarProps) {
  const location = useLocation();
  const branding = useBranding();
  const { isEnabled } = useModules();
  const { user } = useAuth();
  const { canViewCommission } = usePermissions();
  const { role: effectiveRole, preset: effectivePreset } = useEffectiveAuth();
  const { collapsed, toggleCollapsed } = useSidebarCollapsed();
  // Guided-tour orchestration (#1321/#1324): when the admin welcome tour
  // highlights a nav item, expand its accordion group so the target is visible.
  const { navGroupToExpand } = useTour();

  // D8-ANA-W3-005 (#3038): rank-1-only nav items use the SAME effective
  // identity ProtectedRoute's AdminRoute checks, so an admin previewing-as a
  // lower persona never sees a nav entry their preview wouldn't actually reach.
  const effectiveRank = rankFor(effectiveRole, effectivePreset);

  const itemVisible = (item: NavItem) =>
    (!item.module || isEnabled(item.module)) &&
    (!item.superuserOnly || Boolean(user?.is_superuser)) &&
    (!item.rank1Only || effectiveRank === 1) &&
    (!item.requiresCommission || canViewCommission);

  // Build the visible groups (hide groups that end up empty after gating).
  const visibleGroups = adminNavGroups
    .map((group) => ({ ...group, items: group.items.filter(itemVisible) }))
    .filter((group) => group.items.length > 0);

  const groupIds = visibleGroups.map((g) => g.id);

  const activeGroupId =
    visibleGroups.find((group) =>
      group.items.some((item) =>
        item.exact
          ? location.pathname === item.to
          : location.pathname.startsWith(item.to),
      ),
    )?.id ?? null;

  // Clean baseline on every load (#803): only the first group and the active
  // route's group are expanded; everything else collapsed (no persisted pile).
  const { isExpanded, toggleGroup } = useNavGroups(groupIds, activeGroupId);

  // #2579: the desktop rail width only applies at lg:. Below lg: the off-canvas
  // drawer stays full width regardless of the collapse preference.
  const widthClass = collapsed ? "w-64 lg:w-16" : "w-64";

  return (
    <>
      {/* Backdrop for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          role="presentation"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        data-collapsed={collapsed}
        className={`
          fixed top-0 left-0 z-50 h-screen ${widthClass} bg-navy flex flex-col
          transition-[transform,width] duration-200 ease-in-out motion-reduce:transition-none
          lg:translate-x-0 lg:z-auto
          ${isOpen ? "translate-x-0" : "-translate-x-full"}
        `}
      >
        {/* Brand header — shrinks to the logo mark on the desktop rail. */}
        <div
          className={`flex items-center justify-between py-5 border-b border-navy-light ${
            collapsed ? "px-5 lg:px-0 lg:justify-center" : "px-5"
          }`}
        >
          <div
            className={`flex items-center gap-3 ${collapsed ? "lg:gap-0" : ""}`}
          >
            <AgencyLogo variant="compact" surface="dark" size="sm" />
            <span
              className={`text-lg font-semibold text-[#ffffff] ${
                collapsed ? "lg:hidden" : ""
              }`}
            >
              {branding.agency_name}
            </span>
          </div>
          <button
            onClick={onClose}
            className="lg:hidden p-2.5 -mr-1 rounded-lg hover:bg-navy-light transition-colors"
            aria-label="Close sidebar"
          >
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        {/* Nav groups */}
        <nav className={`flex-1 overflow-y-auto py-4 ${collapsed ? "px-2 lg:px-2" : "px-3"}`}>
          <ul className="space-y-1">
            {visibleGroups.map((group, index) => (
              <NavGroupSection
                key={group.id}
                id={group.id}
                label={group.label}
                icon={group.icon}
                items={group.items}
                expanded={isExpanded(group.id) || navGroupToExpand === group.id}
                onToggle={toggleGroup}
                onNavigate={onClose}
                collapsed={collapsed}
                first={index === 0}
              />
            ))}
          </ul>
        </nav>

        {/* Footer */}
        <div className="px-3 py-4 border-t border-navy-light">
          <div
            className={`flex items-center ${
              collapsed
                ? "justify-between lg:flex-col lg:gap-2"
                : "justify-between"
            }`}
          >
            <span
              className={`text-xs text-gray-500 px-2 ${
                collapsed ? "lg:hidden" : ""
              }`}
            >
              Super User
            </span>
            <div className={`flex items-center gap-1 ${collapsed ? "lg:flex-col" : ""}`}>
              <ThemeToggle />
              {/* Desktop-only rail toggle. */}
              <SidebarCollapseToggle
                collapsed={collapsed}
                onToggle={toggleCollapsed}
              />
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
